<template>
	<div class="hidden lg:block">
		<!-- Debug elements -->
		<div
			v-if="debug"
			class="fixed inset-0 pointer-events-none z-50"
		>
			<div
				ref="startTriggerRef"
				class="fixed left-0 w-full h-0.5 flex items-center pointer-events-none"
				:style="{ top: `${startTriggerPoint}px`, backgroundColor: 'rgba(0, 255, 0, 0.5)' }"
			>
				<span class="absolute left-2.5 bg-black/70 text-white text-xs py-0.5 px-1.5 rounded whitespace-nowrap">
					Start Trigger (default 60% viewport height)
				</span>
			</div>

			<div
				ref="endTriggerRef"
				class="fixed left-0 w-full h-0.5 flex items-center pointer-events-none"
				:style="{ top: `${endTriggerPoint}px`, backgroundColor: 'rgba(255, 0, 0, 0.5)' }"
			>
				<span class="absolute left-2.5 bg-black/70 text-white text-xs py-0.5 px-1.5 rounded whitespace-nowrap">
					End Trigger (default 40% viewport height)
				</span>
			</div>

			<div class="fixed right-2.5 top-[70px] bg-black/70 text-white py-1 px-2.5 rounded text-xs space-y-1">
				<div class="flex gap-2">
					Path Completion: <span :class="completionPercentage >= 99.9 ? 'text-red-500' : 'text-white'">
						{{ completionPercentage }}%
					</span>
				</div>
				<div class="flex gap-2">
					Animation: <span :class="isAnimating ? 'text-green-500' : 'text-red-500'">
						{{ isAnimating ? 'Running' : 'Stopped' }}
					</span>
				</div>
				<div class="flex flex-col gap-1">
					<div>
						Direction: {{ scrollingDirection }}
					</div>
					<div>
						End Passed:
						<span :class="hasPassedEndTrigger ? 'text-red-500' : 'text-green-500'">
							{{ hasPassedEndTrigger ? 'Yes' : 'No' }}
						</span>
					</div>
					<div>
						Dynamic Trigger: {{ dynamicStartTrigger ?? '-' }}
					</div>
				</div>
			</div>
		</div>

		<!-- Path container for SVG paths and dots -->
		<div
			ref="pathContainerRef"
			:class="`absolute top-[100px] left-[calc(50%+220px)] -translate-x-1/2 w-[1302px] z-[-1] ${fiberClass}`"
			:style="{ height: `${length}px` }"
		>
			<svg
				width="100%"
				height="100%"
				:viewBox="`0 0 1302 ${length}`"
				fill="none"
				preserveAspectRatio="xMidYMin meet"
				overflow="visible"
			>
				<path
					ref="path1Ref"
					:d="path1d"
					stroke="url(#A)"
					stroke-width="4"
				/>
				<path
					ref="path2Ref"
					:d="path2d"
					stroke="url(#B)"
					stroke-width="2"
				/>

				<!-- Use CSS-based glow instead of SVG filter -->
				<g
					ref="dot1Ref"
					class="transition-opacity duration-200 ease-in-out"
					:class="dotsVisible ? 'opacity-100' : 'opacity-0'"
				>
					<circle
						:cx="dot1Position.x"
						:cy="dot1Position.y"
						r="5"
						fill="#FF5733"
						class="animate-dot-glow"
					/>
				</g>
				<g
					ref="dot2Ref"
					class="transition-opacity duration-200 ease-in-out"
					:class="dotsVisible ? 'opacity-100' : 'opacity-0'"
				>
					<circle
						:cx="dot2Position.x"
						:cy="dot2Position.y"
						r="5"
						fill="#FF5733"
						class="animate-dot-glow"
					/>
				</g>

				<!-- Simplified filter for better performance -->
				<defs>


					<linearGradient
						id="B"
						x1="1294"
						y1="-36.643"
						x2="233.162"
						y2="2609.65"
						gradientUnits="userSpaceOnUse"
					>
						<stop
							stop-color="#ff5733"
							stop-opacity=".05"
						/>
						<stop
							offset=".37"
							stop-color="#ff5733"
						/>
						<stop
							offset="1"
							stop-color="#ee8b2c"
						/>
					</linearGradient>
					<linearGradient
						id="A"
						x1="1281"
						y1="-48.643"
						x2="220.161"
						y2="2597.65"
						gradientUnits="userSpaceOnUse"
					>
						<stop
							stop-color="#ff5733"
							stop-opacity=".05"
						/>
						<stop
							offset=".37"
							stop-color="#ff5733"
						/>
						<stop
							offset="1"
							stop-color="#ee8b2c"
						/>
					</linearGradient>
				</defs>
			</svg>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useFiberAnimation } from '@/composables/useFiberAnimation'

const props = defineProps({
	debug: {
		type: Boolean,
		default: false
	},
	startTriggerViewportPosition: {
		type: Number,
		default: 0.6
	},
	endTriggerViewportPosition: {
		type: Number,
		default: 0.4
	},
	length: {
		type: Number,
		default: 2776
	},
	fiberClass: {
		type: String,
		default: ''
	}
})

const {
	pathContainerRef,
	path1Ref,
	path2Ref,
	dot1Ref,
	dot2Ref,
	dot1Position,
	dot2Position,
	dotsVisible,
	completionPercentage,
	scrollingDirection,
	isAnimating,
	hasPassedEndTrigger,
	dynamicStartTrigger,
	startTriggerPoint,
	endTriggerPoint
} = useFiberAnimation(
	props.startTriggerViewportPosition,
	props.endTriggerViewportPosition
)

const { length } = toRefs(props)

const path1d = computed(() => `M1302 1H902C860.579 1 827 34.5786 827 76V395C827 436.421 793.421 470 752 470H76C34.5786 470 1 503.579 1 545V${length.value}`)
const path2d = computed(() => `M1302 13H915C873.579 13 840 46.5786 840 88V407C840 448.421 806.421 482 765 482H89C47.5786 482 14 515.579 14 557V${length.value}`)
</script>
